/**
 * Calls provided `roll` callback until the desired number of items is obtained.
 *
 * Duplicates are automatically re-rolled (using the getKey return value) to
 * determine identity, as well as values that return true from the optional
 * `shouldReroll` function.
 *
 * A safety circuit breaker makes sure that we don't get stuck in an infinite
 * loop (e.g. roll doesn't return enough unique values, etc...)
 */
export function rollTimes<T>(
  /**
   * How many results should be generated?
   */
  count: number,

  /**
   * The callback to run on every iteration.
   */
  roll: () => T,

  options: {
    /**
     *
     */
    getKey: (item: T) => string;
    shouldReroll?: (item: T, rolled: Map<string, T>) => boolean;
  },
) {
  const { getKey, shouldReroll } = options;

  let breaker = 0;
  const rolled = new Map<string, T>();

  // Generate random items and reroll duplicates
  while (rolled.size < count) {
    const item = roll();
    const key = getKey(item);

    if (!shouldReroll?.(item, rolled) && !rolled.has(key)) {
      rolled.set(key, item);
    }

    if (++breaker > count + 100) {
      console.warn(`Circuit breaker condition was met.`);
      break;
    }
  }

  return Array.from(rolled.values());
}
